/**
 * /dashboard/settings/expense-categories — Settings → Expenses →
 * Expense categories (SOP §8 + §11.2).
 *
 * Reuses the same hook + form components the old
 * /dashboard/expenses/admin/categories page used.
 *
 * Migration note (2026-06-01, Next.js → Vite Phase 3g): direct port of
 * frontend/src/app/(dashboard)/dashboard/settings/expense-categories/page.tsx.
 */

import { useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toApiError } from "@/lib/api";
import { useExpenseCategories } from "@/hooks/use-expenses";
import { formatPaise } from "@/lib/expense-format";
import { useExpenseRoles } from "@/components/expenses/use-expense-roles";
import { CategoryForm } from "@/components/expenses/category-form";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { StatusBadge } from "@/components/shared/status-badge";
import { RestoreDefaultsButton } from "@/components/settings/restore-defaults-button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/expense-categories",
)({
  component: SettingsExpenseCategoriesRoute,
});

function SettingsExpenseCategoriesRoute() {
  const { isExpenseAdmin } = useExpenseRoles();
  const q = useExpenseCategories();

  const categories = useMemo(
    () =>
      [...(q.data?.categories ?? [])].sort(
        (a, b) =>
          a.sort_order - b.sort_order || a.name.localeCompare(b.name),
      ),
    [q.data],
  );

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Expense categories</h2>
              <p className="text-sm text-muted-foreground">
                Claim categories, GL codes, receipt rules and caps.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {isExpenseAdmin && (
                <RestoreDefaultsButton
                  catalog="expense-categories"
                  catalogLabel="expense categories"
                  invalidateKeys={[["expenses", "categories"]]}
                />
              )}
              {isExpenseAdmin && <CategoryForm />}
            </div>
          </div>

          {!isExpenseAdmin && (
            <Alert>
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                Only HR managers and company admins can change expense
                categories.
              </AlertDescription>
            </Alert>
          )}

          {q.isLoading && (
            <div className="space-y-3">
              {["s1", "s2", "s3", "s4"].map((k) => (
                <Skeleton key={k} className="h-20 w-full" />
              ))}
            </div>
          )}

          {q.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn&apos;t load expense categories</AlertTitle>
              <AlertDescription>
                {toApiError(q.error).message}
              </AlertDescription>
            </Alert>
          )}

          {q.data && categories.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                No expense categories yet.
                {isExpenseAdmin &&
                  " Use Restore defaults to seed the standard set."}
              </CardContent>
            </Card>
          )}

          {q.data && categories.length > 0 && (
            <div className="space-y-3">
              {categories.map((c) => (
                <Card key={c.id}>
                  <CardHeader>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <CardTitle className="text-base">{c.name}</CardTitle>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {c.is_active ? (
                            <StatusBadge tone="success" label="Active" />
                          ) : (
                            <StatusBadge tone="neutral" label="Inactive" />
                          )}
                          <StatusBadge
                            tone={c.requires_receipt ? "info" : "neutral"}
                            label={
                              c.requires_receipt
                                ? "Receipt required"
                                : "Receipt optional"
                            }
                          />
                          {c.max_amount_paise != null && (
                            <StatusBadge
                              tone="warning"
                              label={`Cap ${formatPaise(c.max_amount_paise)}`}
                            />
                          )}
                          {c.gl_code && (
                            <StatusBadge
                              tone="neutral"
                              label={`GL ${c.gl_code}`}
                            />
                          )}
                        </div>
                      </div>
                      {isExpenseAdmin && <CategoryForm existing={c} />}
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </div>
      </SettingsShell>
    </>
  );
}
